import { useState } from "react";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import Reveal from "@/components/base/Reveal";

type Voice = "Ambient" | "Focus" | "Late night";

export default function TryPage() {
  const [voice, setVoice] = useState<Voice>("Ambient");
  const [prompt, setPrompt] = useState("");
  const [listening, setListening] = useState(false);

  const prompts = [
    "What's on my quiet list today?",
    "Read the last note from Ines.",
    "Turn the studio lights down slowly.",
    "Remind me to breathe in an hour.",
  ];

  const voices: { name: Voice; note: string }[] = [
    { name: "Ambient", note: "Barely there, mostly listening" },
    { name: "Focus", note: "Short, direct, no small talk" },
    { name: "Late night", note: "Warmer, slower, dimmer" },
  ];

  const responses: Record<Voice, string> = {
    Ambient:
      "Three quiet things. A walk before the room is warm. One letter, unfinished. Tea, before it goes cold.",
    Focus:
      "You have two meetings. One deep block from ten to noon. Nothing else needs you today.",
    "Late night":
      "The house is asleep. I dimmed the studio to twelve percent. Nothing urgent — everything can wait until morning.",
  };

  return (
    <main className="min-w-[1024px] w-full min-h-screen bg-background-50 text-foreground-950">
      {/* Top area */}
      <div className="flex flex-col">
        <SiteNav />

        {/* Section header */}
        <section id="try-hero" className="w-full px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
          <Reveal y={30}>
            <div className="flex items-end justify-between mb-16 md:mb-20">
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                <span>Try — 01</span>
              </p>
              <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500 hidden md:block">
                <span>A quiet console, in the browser</span>
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-14 items-end">
            <Reveal delay={120} y={40}>
              <h1
                className="font-heading font-semibold leading-[0.92] tracking-[-0.03em] text-foreground-950"
                style={{ fontSize: "clamp(56px, 9vw, 140px)" }}
              >
                Try it, <br />
                <span className="italic font-normal text-foreground-700">
                  quietly.
                </span>
              </h1>
            </Reveal>
            <Reveal delay={260} y={40}>
              <div className="max-w-md">
                <p className="text-[13px] leading-[1.75] text-foreground-700 text-justify">
                  Pick a voice, ask something small. This is a taste of Topbud
                  running in a browser — not the full companion, but the same
                  restrained tone. Nothing is stored, nothing is sent home.
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <span className="h-px w-10 bg-foreground-400" />
                  <span className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-600">
                    Demo build · v0.4
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Console */}
        <section className="w-full px-6 md:px-10 pb-24 md:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-6 md:gap-8 items-stretch">
            {/* LEFT — device visual + status */}
            <Reveal y={60} duration={1000}>
              <div className="relative w-full h-full min-h-[520px] rounded-lg overflow-hidden bg-background-100 border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=Extreme%20macro%20close%20up%20of%20a%20matte%20black%20AI%20device%20with%20a%20soft%20warm%20orange%20light%20halo%20gently%20pulsing%20around%20its%20central%20lens%2C%20monochrome%20warm%20gray%20paper%20background%2C%20dramatic%20side%20light%20from%20left%2C%20long%20soft%20shadow%2C%20editorial%20industrial%20design%20photography%2C%20quiet%20ambient%20mood%2C%20refined%20negative%20space&width=1200&height=1500&seq=topbud-try-main&orientation=portrait"
                  alt="Topbud listening"
                  className="w-full h-full object-cover object-top"
                />

                {/* Top-left status */}
                <div className="absolute left-4 top-4 flex items-center gap-3">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      listening ? "bg-accent-500 animate-pulse" : "bg-background-50/60"
                    }`}
                  />
                  <span className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50">
                    {listening ? "Listening" : "Idle"}
                  </span>
                </div>

                {/* Bottom overlay */}
                <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                  <p className="font-label text-[10px] tracking-[0.22em] uppercase text-background-50/70">
                    Voice — {voice}
                  </p>
                  <p className="mt-3 font-heading text-[26px] md:text-[32px] leading-[1.15] tracking-[-0.02em] text-background-50 max-w-md">
                    {listening
                      ? `"${prompt || "…"}"`
                      : "Say something small. I'm here."}
                  </p>
                  {listening && (
                    <p className="mt-4 text-[13px] leading-[1.7] text-background-50/80 max-w-md">
                      {responses[voice]}
                    </p>
                  )}
                </div>
              </div>
            </Reveal>

            {/* RIGHT — controls */}
            <Reveal delay={180} y={40} className="w-full">
              <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8 h-full flex flex-col">
                {/* Voice picker */}
                <div>
                  <div className="flex items-center justify-between">
                    <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                      Voice
                    </p>
                    <p className="font-label text-[10px] tracking-[0.22em] uppercase text-foreground-400">
                      3 presets
                    </p>
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    {voices.map((v) => {
                      const active = v.name === voice;
                      return (
                        <button
                          key={v.name}
                          type="button"
                          onClick={() => setVoice(v.name)}
                          className={`cursor-pointer whitespace-nowrap text-left rounded-md px-3 py-3 border transition-colors duration-200 ${
                            active
                              ? "border-foreground-950 bg-foreground-950 text-background-50"
                              : "border-background-300 bg-background-50 text-foreground-800 hover:border-foreground-500"
                          }`}
                        >
                          <span className="block font-heading text-[15px] leading-none tracking-[-0.01em]">
                            {v.name}
                          </span>
                          <span
                            className={`mt-2 block font-label text-[9px] tracking-[0.18em] uppercase ${
                              active
                                ? "text-background-50/70"
                                : "text-foreground-500"
                            }`}
                          >
                            {v.note}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Prompt suggestions */}
                <div className="mt-8">
                  <p className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500">
                    Try asking
                  </p>
                  <ul className="mt-4 space-y-2">
                    {prompts.map((p) => (
                      <li key={p}>
                        <button
                          type="button"
                          onClick={() => setPrompt(p)}
                          className="cursor-pointer w-full text-left flex items-center justify-between gap-4 py-3 px-4 rounded-md border border-background-200 hover:border-foreground-500 transition-colors duration-200"
                        >
                          <span className="text-[13px] text-foreground-800">
                            {p}
                          </span>
                          <i className="ri-arrow-right-up-line text-[14px] text-foreground-500" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Prompt input + listen */}
                <div className="mt-8">
                  <label
                    htmlFor="try-prompt"
                    className="font-label text-[11px] tracking-[0.22em] uppercase text-foreground-500"
                  >
                    Or say your own
                  </label>
                  <div className="mt-3 flex items-stretch gap-3 border-b border-foreground-300 pb-2">
                    <input
                      id="try-prompt"
                      type="text"
                      value={prompt}
                      maxLength={140}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Type a small, ordinary sentence…"
                      className="flex-1 bg-transparent outline-none text-[15px] text-foreground-950 placeholder:text-foreground-400 py-2"
                    />
                    <button
                      type="button"
                      onClick={() => setListening((s) => !s)}
                      className={`cursor-pointer whitespace-nowrap inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-[11px] font-label tracking-[0.18em] uppercase transition-colors duration-200 ${
                        listening
                          ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                          : "bg-primary-500 text-background-50 dark:text-foreground-950 hover:bg-primary-600"
                      }`}
                    >
                      <span>{listening ? "Stop" : "Listen"}</span>
                      <i
                        className={
                          listening
                            ? "ri-stop-circle-line text-[13px]"
                            : "ri-mic-line text-[13px]"
                        }
                      />
                    </button>
                  </div>
                  <p className="mt-3 font-label text-[10px] tracking-[0.22em] uppercase text-foreground-500">
                    {prompt.length}/140 · Nothing is stored
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </div>

      <SiteFooter />
    </main>
  );
}